import React, { useMemo } from 'react';
import { AlertTriangle } from 'lucide-react';
import { cn } from '../../lib/utils';
import { ChartContainer, type ChartSeries } from '../charts/chart-container';

// Type definitions
export type WidgetType =
  | 'kpi'
  | 'bar_chart'
  | 'line_chart'
  | 'area_chart'
  | 'pie_chart'
  | 'donut_chart'
  | 'data_table'
  | 'text'
  | 'metric_group'
  | 'gauge'
  | 'number_grid'
  | 'progress_bar'
  | 'sparkline'
  | 'heatmap'
  | 'funnel'
  | 'scatter'
  | 'leaderboard'
  | 'timeline'
  | 'map'
  | 'calendar_heat'
  | 'custom';

export interface WidgetConfig {
  id: string;
  type: WidgetType;
  title: string;
  data?: any;
  config?: Record<string, unknown>;
}

export interface WidgetRendererProps {
  widget: WidgetConfig;
  isLoading?: boolean;
  className?: string;
}

// Widget render function type
type WidgetRenderFunction = (
  config: WidgetConfig,
  isLoading?: boolean
) => React.ReactNode;

// Global widget registry
const widgetRegistry = new Map<WidgetType, WidgetRenderFunction>();

/**
 * Register a custom widget type renderer
 */
export function registerWidgetType(
  type: WidgetType,
  renderer: WidgetRenderFunction
): void {
  widgetRegistry.set(type, renderer);
}

/**
 * Unregister a widget type
 */
export function unregisterWidgetType(type: WidgetType): void {
  widgetRegistry.delete(type);
}

/**
 * Get registered widget type renderer
 */
export function getWidgetRenderer(type: WidgetType): WidgetRenderFunction | undefined {
  return widgetRegistry.get(type);
}

// Default widget renderers

/**
 * KPI Widget - simple metric display
 */
function renderKpiWidget(config: WidgetConfig, isLoading?: boolean): React.ReactNode {
  const { data, config: widgetConfig } = config;
  const value = data?.value ?? 0;
  const format = (widgetConfig?.format as string) ?? 'number';
  const trend = data?.trend ?? 0;
  const unit = (widgetConfig?.unit as string) ?? '';

  let formattedValue = String(value);
  if (format === 'currency') {
    formattedValue = new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(value);
  } else if (format === 'percent') {
    formattedValue = `${(value * 100).toFixed(1)}%`;
  } else if (format === 'number') {
    formattedValue = new Intl.NumberFormat('en-IN').format(value);
  }

  return (
    <div className="space-y-2">
      <div className="text-sm font-medium text-muted-foreground">Value</div>
      <div className="flex items-baseline gap-2">
        <div className="text-3xl font-bold text-foreground">{formattedValue}</div>
        {unit && <span className="text-sm text-muted-foreground">{unit}</span>}
      </div>
      {trend !== 0 && (
        <div
          className={cn('text-xs font-medium', {
            'text-green-600': trend > 0,
            'text-red-600': trend < 0,
          })}
        >
          {trend > 0 ? '+' : ''}{trend.toFixed(1)}% vs last period
        </div>
      )}
    </div>
  );
}

/**
 * Text Widget - plain text display
 */
function renderTextWidget(config: WidgetConfig): React.ReactNode {
  const { data, config: widgetConfig } = config;
  const content = data?.content ?? '';
  const align = (widgetConfig?.align as 'left' | 'center' | 'right') ?? 'left';
  const size = (widgetConfig?.size as 'sm' | 'md' | 'lg') ?? 'md';

  const sizeClasses = {
    sm: 'text-sm',
    md: 'text-base',
    lg: 'text-lg',
  };

  const alignClasses = {
    left: 'text-left',
    center: 'text-center',
    right: 'text-right',
  };

  return (
    <div className={cn(sizeClasses[size], alignClasses[align], 'text-foreground whitespace-pre-wrap')}>
      {content}
    </div>
  );
}

/**
 * Bar Chart Widget
 */
function renderBarChartWidget(config: WidgetConfig): React.ReactNode {
  const { data, config: widgetConfig } = config;
  const series = data?.series as ChartSeries[] | undefined;
  const categories = data?.categories as string[] | undefined;

  if (!series || !categories) {
    return <div className="text-xs text-muted-foreground">No data available</div>;
  }

  return (
    <ChartContainer
      type="bar"
      series={series}
      categories={categories}
      height={(widgetConfig?.height as number) ?? 300}
      options={widgetConfig?.options as Record<string, unknown>}
    />
  );
}

/**
 * Line Chart Widget
 */
function renderLineChartWidget(config: WidgetConfig): React.ReactNode {
  const { data, config: widgetConfig } = config;
  const series = data?.series as ChartSeries[] | undefined;
  const categories = data?.categories as string[] | undefined;

  if (!series || !categories) {
    return <div className="text-xs text-muted-foreground">No data available</div>;
  }

  return (
    <ChartContainer
      type="line"
      series={series}
      categories={categories}
      height={(widgetConfig?.height as number) ?? 300}
      options={widgetConfig?.options as Record<string, unknown>}
    />
  );
}

/**
 * Area Chart Widget
 */
function renderAreaChartWidget(config: WidgetConfig): React.ReactNode {
  const { data, config: widgetConfig } = config;
  const series = data?.series as ChartSeries[] | undefined;
  const categories = data?.categories as string[] | undefined;

  if (!series || !categories) {
    return <div className="text-xs text-muted-foreground">No data available</div>;
  }

  return (
    <ChartContainer
      type="area"
      series={series}
      categories={categories}
      height={(widgetConfig?.height as number) ?? 300}
      options={widgetConfig?.options as Record<string, unknown>}
    />
  );
}

/**
 * Pie Chart Widget
 */
function renderPieChartWidget(config: WidgetConfig): React.ReactNode {
  const { data, config: widgetConfig } = config;
  const series = data?.series as ChartSeries[] | undefined;
  const labels = data?.labels as string[] | undefined;

  if (!series || !labels) {
    return <div className="text-xs text-muted-foreground">No data available</div>;
  }

  return (
    <ChartContainer
      type="pie"
      series={series}
      categories={labels}
      height={(widgetConfig?.height as number) ?? 300}
      options={widgetConfig?.options as Record<string, unknown>}
    />
  );
}

/**
 * Donut Chart Widget
 */
function renderDonutChartWidget(config: WidgetConfig): React.ReactNode {
  const { data, config: widgetConfig } = config;
  const series = data?.series as ChartSeries[] | undefined;
  const labels = data?.labels as string[] | undefined;

  if (!series || !labels) {
    return <div className="text-xs text-muted-foreground">No data available</div>;
  }

  return (
    <ChartContainer
      type="donut"
      series={series}
      categories={labels}
      height={(widgetConfig?.height as number) ?? 300}
      options={widgetConfig?.options as Record<string, unknown>}
    />
  );
}

/**
 * Data Table Widget
 */
function renderDataTableWidget(config: WidgetConfig): React.ReactNode {
  const { data, config: widgetConfig } = config;
  const rows = data?.rows as Array<Record<string, unknown>> | undefined;
  const columns = data?.columns as string[] | undefined;

  if (!rows || !columns) {
    return <div className="text-xs text-muted-foreground">No data available</div>;
  }

  const maxRows = (widgetConfig?.maxRows as number) ?? 10;
  const displayRows = rows.slice(0, maxRows);

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="border-b border-border">
            {columns.map((col) => (
              <th
                key={col}
                className="px-3 py-2 text-left font-medium text-muted-foreground"
              >
                {col}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {displayRows.map((row, idx) => (
            <tr key={idx} className="border-b border-border hover:bg-muted/50">
              {columns.map((col) => (
                <td key={`${idx}-${col}`} className="px-3 py-2 text-foreground">
                  {String(row[col] ?? '-')}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {rows.length > maxRows && (
        <div className="text-xs text-muted-foreground pt-2">
          Showing {maxRows} of {rows.length} rows
        </div>
      )}
    </div>
  );
}

/**
 * Metric Group Widget - display multiple metrics
 */
function renderMetricGroupWidget(config: WidgetConfig): React.ReactNode {
  const { data, config: widgetConfig } = config;
  const metrics = data?.metrics as Array<{ label: string; value: any; format?: string }> | undefined;

  if (!metrics) {
    return <div className="text-xs text-muted-foreground">No data available</div>;
  }

  const columns = (widgetConfig?.columns as number) ?? 2;
  const gridColsMap: Record<number, string> = {
    1: 'grid-cols-1',
    2: 'grid-cols-2',
    3: 'grid-cols-3',
    4: 'grid-cols-4',
  };

  return (
    <div className={cn('grid gap-4', gridColsMap[Math.min(columns, 4)] || 'grid-cols-2')}>
      {metrics.map((metric, idx) => {
        const format = metric.format ?? 'number';
        let formattedValue = String(metric.value);

        if (format === 'currency') {
          formattedValue = new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            maximumFractionDigits: 0,
          }).format(metric.value);
        } else if (format === 'percent') {
          formattedValue = `${(metric.value * 100).toFixed(1)}%`;
        } else if (format === 'number') {
          formattedValue = new Intl.NumberFormat('en-IN').format(metric.value);
        }

        return (
          <div
            key={idx}
            className="p-3 rounded-lg bg-muted/50 border border-border"
          >
            <div className="text-xs font-medium text-muted-foreground">
              {metric.label}
            </div>
            <div className="text-2xl font-bold text-foreground mt-1">
              {formattedValue}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/**
 * Gauge Widget - circular progress indicator
 */
function renderGaugeWidget(config: WidgetConfig): React.ReactNode {
  const { data, config: widgetConfig } = config;
  const value = data?.value ?? 0;
  const max = (widgetConfig?.max as number) ?? 100;
  const min = (widgetConfig?.min as number) ?? 0;
  const unit = (widgetConfig?.unit as string) ?? '';
  const label = data?.label ?? '';

  const percentage = Math.min(100, Math.max(0, ((value - min) / (max - min)) * 100));
  const radius = 60;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;

  const getColor = () => {
    if (percentage >= 80) return 'text-green-500';
    if (percentage >= 50) return 'text-yellow-500';
    return 'text-red-500';
  };

  let formattedValue = String(value);
  const format = (widgetConfig?.format as string) ?? 'number';
  if (format === 'currency') {
    formattedValue = new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(value);
  } else if (format === 'percent') {
    formattedValue = `${value.toFixed(1)}%`;
  } else if (format === 'number') {
    formattedValue = new Intl.NumberFormat('en-IN').format(value);
  }

  return (
    <div className="flex flex-col items-center justify-center gap-2">
      <div className="relative">
        <svg width="140" height="140" viewBox="0 0 140 140" className="-rotate-90">
          <circle
            cx="70"
            cy="70"
            r={radius}
            fill="none"
            strokeWidth="10"
            className="stroke-muted"
          />
          <circle
            cx="70"
            cy="70"
            r={radius}
            fill="none"
            strokeWidth="10"
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            className={cn('transition-all duration-500', getColor().replace('text-', 'stroke-'))}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-2xl font-bold text-foreground">{formattedValue}</span>
          {unit && <span className="text-xs text-muted-foreground">{unit}</span>}
        </div>
      </div>
      {label && <div className="text-sm font-medium text-muted-foreground">{label}</div>}
    </div>
  );
}

/**
 * Number Grid Widget - grid of key numbers
 */
function renderNumberGridWidget(config: WidgetConfig): React.ReactNode {
  const { data, config: widgetConfig } = config;
  const items = data?.items as Array<{ label: string; value: number; format?: string; icon?: string }> | undefined;

  if (!items || items.length === 0) {
    return <div className="text-xs text-muted-foreground">No data available</div>;
  }

  const columns = (widgetConfig?.columns as number) ?? 3;
  const gridColsMap: Record<number, string> = {
    1: 'grid-cols-1',
    2: 'grid-cols-2',
    3: 'grid-cols-3',
    4: 'grid-cols-4',
    5: 'grid-cols-5',
  };

  return (
    <div className={cn('grid gap-3', gridColsMap[Math.min(columns, 5)] || 'grid-cols-3')}>
      {items.map((item, idx) => {
        const format = item.format ?? 'number';
        let formattedValue = String(item.value);

        if (format === 'currency') {
          formattedValue = new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            maximumFractionDigits: 0,
          }).format(item.value);
        } else if (format === 'percent') {
          formattedValue = `${(item.value * 100).toFixed(1)}%`;
        } else if (format === 'number') {
          formattedValue = new Intl.NumberFormat('en-IN').format(item.value);
        }

        return (
          <div key={idx} className="text-center p-2">
            <div className="text-xl font-bold text-foreground">{formattedValue}</div>
            <div className="text-xs text-muted-foreground mt-1">{item.label}</div>
          </div>
        );
      })}
    </div>
  );
}

/**
 * Progress Bar Widget - horizontal progress indicators
 */
function renderProgressBarWidget(config: WidgetConfig): React.ReactNode {
  const { data, config: widgetConfig } = config;
  const items = data?.items as Array<{ label: string; value: number; max?: number; color?: string }> | undefined;

  if (!items || items.length === 0) {
    return <div className="text-xs text-muted-foreground">No data available</div>;
  }

  const showPercentage = (widgetConfig?.showPercentage as boolean) ?? true;

  return (
    <div className="space-y-4">
      {items.map((item, idx) => {
        const max = item.max ?? 100;
        const percentage = Math.min(100, Math.max(0, (item.value / max) * 100));
        const colorClass = item.color ?? 'bg-primary';

        return (
          <div key={idx} className="space-y-1.5">
            <div className="flex items-center justify-between text-sm">
              <span className="font-medium text-foreground">{item.label}</span>
              {showPercentage && (
                <span className="text-muted-foreground">{percentage.toFixed(0)}%</span>
              )}
            </div>
            <div className="h-2 rounded-full bg-muted overflow-hidden">
              <div
                className={cn('h-full rounded-full transition-all duration-500', colorClass)}
                style={{ width: `${percentage}%` }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

/**
 * Sparkline Widget - inline mini line chart
 */
function renderSparklineWidget(config: WidgetConfig): React.ReactNode {
  const { data, config: widgetConfig } = config;
  const values = data?.values as number[] | undefined;
  const label = data?.label ?? '';
  const currentValue = data?.currentValue ?? (values ? values[values.length - 1] : 0);

  if (!values || values.length === 0) {
    return <div className="text-xs text-muted-foreground">No data available</div>;
  }

  const format = (widgetConfig?.format as string) ?? 'number';
  let formattedValue = String(currentValue);
  if (format === 'currency') {
    formattedValue = new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(currentValue);
  } else if (format === 'percent') {
    formattedValue = `${(currentValue * 100).toFixed(1)}%`;
  } else if (format === 'number') {
    formattedValue = new Intl.NumberFormat('en-IN').format(currentValue);
  }

  // Build SVG sparkline path
  const width = 200;
  const height = 40;
  const padding = 2;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;

  const points = values.map((v, i) => {
    const x = padding + (i / (values.length - 1)) * (width - 2 * padding);
    const y = height - padding - ((v - min) / range) * (height - 2 * padding);
    return `${x},${y}`;
  });

  const pathD = `M${points.join(' L')}`;
  const trend = values.length >= 2 ? values[values.length - 1] - values[0] : 0;

  return (
    <div className="space-y-2">
      <div className="flex items-baseline justify-between">
        <div>
          {label && <div className="text-xs text-muted-foreground">{label}</div>}
          <div className="text-2xl font-bold text-foreground">{formattedValue}</div>
        </div>
        {trend !== 0 && (
          <div
            className={cn('text-xs font-medium', {
              'text-green-600': trend > 0,
              'text-red-600': trend < 0,
            })}
          >
            {trend > 0 ? '+' : ''}{((trend / (values[0] || 1)) * 100).toFixed(1)}%
          </div>
        )}
      </div>
      <svg
        width="100%"
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="none"
        className="overflow-visible"
      >
        <path
          d={pathD}
          fill="none"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className={cn(trend >= 0 ? 'stroke-green-500' : 'stroke-red-500')}
        />
      </svg>
    </div>
  );
}

/**
 * Heatmap Widget - grid-based heatmap visualization
 */
function renderHeatmapWidget(config: WidgetConfig): React.ReactNode {
  const { data, config: widgetConfig } = config;
  const rows = data?.rows as Array<{ label: string; values: number[] }> | undefined;
  const columnLabels = data?.columnLabels as string[] | undefined;

  if (!rows || !columnLabels) {
    return <div className="text-xs text-muted-foreground">No data available</div>;
  }

  const allValues = rows.flatMap((r) => r.values);
  const min = Math.min(...allValues);
  const max = Math.max(...allValues);
  const range = max - min || 1;

  const getHeatColor = (value: number): string => {
    const intensity = (value - min) / range;
    if (intensity < 0.2) return 'bg-blue-100 text-blue-800';
    if (intensity < 0.4) return 'bg-blue-200 text-blue-800';
    if (intensity < 0.6) return 'bg-blue-300 text-blue-900';
    if (intensity < 0.8) return 'bg-blue-400 text-white';
    return 'bg-blue-600 text-white';
  };

  const cellSize = (widgetConfig?.cellSize as number) ?? 40;

  return (
    <div className="overflow-x-auto">
      <table className="border-collapse">
        <thead>
          <tr>
            <th className="text-xs text-muted-foreground p-1" />
            {columnLabels.map((col) => (
              <th key={col} className="text-xs text-muted-foreground p-1 text-center font-medium">
                {col}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, rowIdx) => (
            <tr key={rowIdx}>
              <td className="text-xs text-muted-foreground p-1 font-medium whitespace-nowrap">
                {row.label}
              </td>
              {row.values.map((val, colIdx) => (
                <td key={colIdx} className="p-0.5">
                  <div
                    className={cn(
                      'flex items-center justify-center rounded text-xs font-medium',
                      getHeatColor(val)
                    )}
                    style={{ width: cellSize, height: cellSize }}
                    title={`${row.label} × ${columnLabels[colIdx]}: ${val}`}
                  >
                    {val}
                  </div>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Funnel Widget - conversion funnel visualization
 */
function renderFunnelWidget(config: WidgetConfig): React.ReactNode {
  const { data, config: widgetConfig } = config;
  const steps = data?.steps as Array<{ label: string; value: number; color?: string }> | undefined;

  if (!steps || steps.length === 0) {
    return <div className="text-xs text-muted-foreground">No data available</div>;
  }

  const maxValue = steps[0]?.value ?? 1;
  const showConversion = (widgetConfig?.showConversion as boolean) ?? true;

  const defaultColors = [
    'bg-blue-500', 'bg-blue-400', 'bg-sky-400', 'bg-cyan-400',
    'bg-teal-400', 'bg-emerald-400', 'bg-green-400',
  ];

  return (
    <div className="space-y-2">
      {steps.map((step, idx) => {
        const widthPercent = Math.max(20, (step.value / maxValue) * 100);
        const conversionRate = idx > 0 ? ((step.value / steps[idx - 1].value) * 100).toFixed(1) : null;
        const colorClass = step.color ?? defaultColors[idx % defaultColors.length];

        return (
          <div key={idx} className="space-y-1">
            <div className="flex items-center justify-between text-sm">
              <span className="font-medium text-foreground">{step.label}</span>
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground">
                  {new Intl.NumberFormat('en-IN').format(step.value)}
                </span>
                {showConversion && conversionRate && (
                  <span className="text-xs text-muted-foreground">({conversionRate}%)</span>
                )}
              </div>
            </div>
            <div className="flex justify-center">
              <div
                className={cn('h-8 rounded transition-all duration-300', colorClass)}
                style={{ width: `${widthPercent}%` }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

/**
 * Scatter Widget - scatter plot using SVG
 */
function renderScatterWidget(config: WidgetConfig): React.ReactNode {
  const { data, config: widgetConfig } = config;
  const points = data?.points as Array<{ x: number; y: number; label?: string; size?: number }> | undefined;

  if (!points || points.length === 0) {
    return <div className="text-xs text-muted-foreground">No data available</div>;
  }

  const xLabel = (widgetConfig?.xLabel as string) ?? 'X';
  const yLabel = (widgetConfig?.yLabel as string) ?? 'Y';
  const width = 300;
  const height = 200;
  const padding = 30;

  const xValues = points.map((p) => p.x);
  const yValues = points.map((p) => p.y);
  const xMin = Math.min(...xValues);
  const xMax = Math.max(...xValues);
  const yMin = Math.min(...yValues);
  const yMax = Math.max(...yValues);
  const xRange = xMax - xMin || 1;
  const yRange = yMax - yMin || 1;

  return (
    <div className="flex flex-col items-center">
      <svg width="100%" height={height + 20} viewBox={`0 0 ${width} ${height + 20}`} preserveAspectRatio="xMidYMid meet">
        {/* Grid lines */}
        {[0, 0.25, 0.5, 0.75, 1].map((frac) => (
          <line
            key={`h-${frac}`}
            x1={padding}
            y1={padding + (1 - frac) * (height - 2 * padding)}
            x2={width - padding}
            y2={padding + (1 - frac) * (height - 2 * padding)}
            className="stroke-muted"
            strokeWidth="0.5"
          />
        ))}
        {/* Points */}
        {points.map((point, idx) => {
          const cx = padding + ((point.x - xMin) / xRange) * (width - 2 * padding);
          const cy = padding + (1 - (point.y - yMin) / yRange) * (height - 2 * padding);
          const r = point.size ?? 4;
          return (
            <circle
              key={idx}
              cx={cx}
              cy={cy}
              r={r}
              className="fill-primary/70 stroke-primary"
              strokeWidth="1"
            >
              {point.label && <title>{point.label}: ({point.x}, {point.y})</title>}
            </circle>
          );
        })}
        {/* Axis labels */}
        <text x={width / 2} y={height + 15} textAnchor="middle" className="fill-muted-foreground text-[10px]">{xLabel}</text>
        <text x={10} y={height / 2} textAnchor="middle" transform={`rotate(-90, 10, ${height / 2})`} className="fill-muted-foreground text-[10px]">{yLabel}</text>
      </svg>
    </div>
  );
}

/**
 * Leaderboard Widget - ranked list
 */
function renderLeaderboardWidget(config: WidgetConfig): React.ReactNode {
  const { data, config: widgetConfig } = config;
  const items = data?.items as Array<{ rank?: number; name: string; value: number; avatar?: string; change?: number }> | undefined;

  if (!items || items.length === 0) {
    return <div className="text-xs text-muted-foreground">No data available</div>;
  }

  const maxItems = (widgetConfig?.maxItems as number) ?? 10;
  const format = (widgetConfig?.format as string) ?? 'number';
  const displayItems = items.slice(0, maxItems);
  const maxValue = Math.max(...displayItems.map((i) => i.value));

  const formatValue = (val: number): string => {
    if (format === 'currency') {
      return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(val);
    }
    if (format === 'percent') return `${(val * 100).toFixed(1)}%`;
    return new Intl.NumberFormat('en-IN').format(val);
  };

  return (
    <div className="space-y-2">
      {displayItems.map((item, idx) => {
        const barWidth = (item.value / maxValue) * 100;
        const rank = item.rank ?? idx + 1;

        return (
          <div key={idx} className="flex items-center gap-3">
            <span className={cn(
              'text-sm font-bold w-6 text-center',
              rank <= 3 ? 'text-primary' : 'text-muted-foreground'
            )}>
              {rank}
            </span>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-medium text-foreground truncate">{item.name}</span>
                <div className="flex items-center gap-1">
                  <span className="text-sm text-muted-foreground">{formatValue(item.value)}</span>
                  {item.change !== undefined && item.change !== 0 && (
                    <span className={cn('text-xs', item.change > 0 ? 'text-green-600' : 'text-red-600')}>
                      {item.change > 0 ? '▲' : '▼'}{Math.abs(item.change)}
                    </span>
                  )}
                </div>
              </div>
              <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                <div className="h-full rounded-full bg-primary/70 transition-all duration-300" style={{ width: `${barWidth}%` }} />
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/**
 * Timeline Widget - chronological event list
 */
function renderTimelineWidget(config: WidgetConfig): React.ReactNode {
  const { data, config: widgetConfig } = config;
  const events = data?.events as Array<{ date: string; title: string; description?: string; type?: string }> | undefined;

  if (!events || events.length === 0) {
    return <div className="text-xs text-muted-foreground">No data available</div>;
  }

  const maxEvents = (widgetConfig?.maxEvents as number) ?? 10;
  const displayEvents = events.slice(0, maxEvents);

  const typeColors: Record<string, string> = {
    success: 'bg-green-500',
    warning: 'bg-yellow-500',
    error: 'bg-red-500',
    info: 'bg-blue-500',
    default: 'bg-muted-foreground',
  };

  return (
    <div className="space-y-0">
      {displayEvents.map((event, idx) => {
        const dotColor = typeColors[event.type ?? 'default'] ?? typeColors.default;
        const isLast = idx === displayEvents.length - 1;

        return (
          <div key={idx} className="flex gap-3">
            <div className="flex flex-col items-center">
              <div className={cn('w-2.5 h-2.5 rounded-full mt-1.5 flex-shrink-0', dotColor)} />
              {!isLast && <div className="w-px flex-1 bg-border" />}
            </div>
            <div className={cn('pb-4', isLast && 'pb-0')}>
              <div className="text-xs text-muted-foreground">{event.date}</div>
              <div className="text-sm font-medium text-foreground">{event.title}</div>
              {event.description && (
                <div className="text-xs text-muted-foreground mt-0.5">{event.description}</div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/**
 * Map Widget - placeholder for geo visualization
 */
function renderMapWidget(config: WidgetConfig): React.ReactNode {
  const { data, config: widgetConfig } = config;
  const regions = data?.regions as Array<{ name: string; value: number; lat?: number; lng?: number }> | undefined;

  if (!regions || regions.length === 0) {
    return <div className="text-xs text-muted-foreground">No data available</div>;
  }

  // SVG-based simple region list (full map would need a mapping library)
  const maxValue = Math.max(...regions.map((r) => r.value));
  const format = (widgetConfig?.format as string) ?? 'number';

  const formatValue = (val: number): string => {
    if (format === 'currency') {
      return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(val);
    }
    return new Intl.NumberFormat('en-IN').format(val);
  };

  return (
    <div className="space-y-2">
      <div className="text-xs text-muted-foreground mb-2">Regional Distribution</div>
      {regions.map((region, idx) => {
        const percentage = (region.value / maxValue) * 100;
        return (
          <div key={idx} className="flex items-center gap-3">
            <div className="w-24 text-sm text-foreground truncate">{region.name}</div>
            <div className="flex-1 h-4 rounded bg-muted overflow-hidden">
              <div
                className="h-full rounded bg-primary/60 transition-all duration-300"
                style={{ width: `${percentage}%` }}
              />
            </div>
            <div className="w-20 text-sm text-muted-foreground text-right">{formatValue(region.value)}</div>
          </div>
        );
      })}
    </div>
  );
}

/**
 * Calendar Heat Widget - GitHub-style contribution calendar
 */
function renderCalendarHeatWidget(config: WidgetConfig): React.ReactNode {
  const { data, config: widgetConfig } = config;
  const days = data?.days as Array<{ date: string; value: number }> | undefined;

  if (!days || days.length === 0) {
    return <div className="text-xs text-muted-foreground">No data available</div>;
  }

  const cellSize = (widgetConfig?.cellSize as number) ?? 12;
  const cellGap = 2;

  const allValues = days.map((d) => d.value);
  const maxVal = Math.max(...allValues);
  const minVal = Math.min(...allValues);
  const range = maxVal - minVal || 1;

  const getIntensityClass = (value: number): string => {
    const intensity = (value - minVal) / range;
    if (value === 0) return 'bg-muted';
    if (intensity < 0.25) return 'bg-green-200';
    if (intensity < 0.5) return 'bg-green-300';
    if (intensity < 0.75) return 'bg-green-400';
    return 'bg-green-600';
  };

  // Group days by week (7 rows)
  const weeks: Array<Array<{ date: string; value: number } | null>> = [];
  const startDate = new Date(days[0].date);
  const startDay = startDate.getDay();

  // Pad start
  let currentWeek: Array<{ date: string; value: number } | null> = Array(startDay).fill(null);

  days.forEach((day) => {
    currentWeek.push(day);
    if (currentWeek.length === 7) {
      weeks.push(currentWeek);
      currentWeek = [];
    }
  });
  if (currentWeek.length > 0) {
    while (currentWeek.length < 7) currentWeek.push(null);
    weeks.push(currentWeek);
  }

  return (
    <div className="overflow-x-auto">
      <div className="flex gap-0.5">
        {weeks.map((week, weekIdx) => (
          <div key={weekIdx} className="flex flex-col gap-0.5">
            {week.map((day, dayIdx) => (
              <div
                key={dayIdx}
                className={cn(
                  'rounded-sm transition-colors',
                  day ? getIntensityClass(day.value) : 'bg-transparent'
                )}
                style={{ width: cellSize, height: cellSize }}
                title={day ? `${day.date}: ${day.value}` : ''}
              />
            ))}
          </div>
        ))}
      </div>
      <div className="flex items-center gap-1 mt-2 text-xs text-muted-foreground">
        <span>Less</span>
        {['bg-muted', 'bg-green-200', 'bg-green-300', 'bg-green-400', 'bg-green-600'].map((cls, i) => (
          <div key={i} className={cn('rounded-sm', cls)} style={{ width: cellSize, height: cellSize }} />
        ))}
        <span>More</span>
      </div>
    </div>
  );
}

/**
 * Custom Widget - fallback for unregistered types
 */
function renderCustomWidget(config: WidgetConfig): React.ReactNode {
  return (
    <div className="p-4 rounded-lg bg-muted border border-border">
      <div className="text-sm text-muted-foreground">
        Custom widget: {config.id}
      </div>
    </div>
  );
}

/**
 * Unknown Widget - error state for unregistered types
 */
function renderUnknownWidget(config: WidgetConfig): React.ReactNode {
  return (
    <div className="p-4 rounded-lg bg-destructive/10 border border-destructive/30 flex gap-3">
      <AlertTriangle className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
      <div>
        <div className="text-sm font-medium text-destructive">Unknown Widget Type</div>
        <div className="text-xs text-destructive/80">
          Widget type "{config.type}" is not registered.
        </div>
      </div>
    </div>
  );
}

// Initialize default renderers
function initializeDefaultRegistry(): void {
  widgetRegistry.set('kpi', renderKpiWidget);
  widgetRegistry.set('bar_chart', renderBarChartWidget);
  widgetRegistry.set('line_chart', renderLineChartWidget);
  widgetRegistry.set('area_chart', renderAreaChartWidget);
  widgetRegistry.set('pie_chart', renderPieChartWidget);
  widgetRegistry.set('donut_chart', renderDonutChartWidget);
  widgetRegistry.set('data_table', renderDataTableWidget);
  widgetRegistry.set('text', renderTextWidget);
  widgetRegistry.set('metric_group', renderMetricGroupWidget);
  widgetRegistry.set('gauge', renderGaugeWidget);
  widgetRegistry.set('number_grid', renderNumberGridWidget);
  widgetRegistry.set('progress_bar', renderProgressBarWidget);
  widgetRegistry.set('sparkline', renderSparklineWidget);
  widgetRegistry.set('heatmap', renderHeatmapWidget);
  widgetRegistry.set('funnel', renderFunnelWidget);
  widgetRegistry.set('scatter', renderScatterWidget);
  widgetRegistry.set('leaderboard', renderLeaderboardWidget);
  widgetRegistry.set('timeline', renderTimelineWidget);
  widgetRegistry.set('map', renderMapWidget);
  widgetRegistry.set('calendar_heat', renderCalendarHeatWidget);
  widgetRegistry.set('custom', renderCustomWidget);
}

// Initialize on module load
initializeDefaultRegistry();

/**
 * WidgetRenderer Component
 * Renders widgets based on their type using the registry
 */
export const WidgetRenderer: React.FC<WidgetRendererProps> = ({
  widget,
  isLoading = false,
  className,
}) => {
  const renderer = useMemo(() => {
    return widgetRegistry.get(widget.type) ?? (() => renderUnknownWidget(widget));
  }, [widget.type]);

  const content = useMemo(() => {
    if (isLoading) {
      return (
        <div className="space-y-2">
          <div className="h-8 bg-muted rounded animate-pulse" />
          <div className="h-4 bg-muted rounded animate-pulse" />
        </div>
      );
    }

    try {
      return renderer(widget, isLoading);
    } catch (error) {
      console.error(`Error rendering widget ${widget.id}:`, error);
      return (
        <div className="p-4 rounded-lg bg-destructive/10 border border-destructive/30 flex gap-3">
          <AlertTriangle className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
          <div>
            <div className="text-sm font-medium text-destructive">Render Error</div>
            <div className="text-xs text-destructive/80">
              Failed to render widget: {error instanceof Error ? error.message : 'Unknown error'}
            </div>
          </div>
        </div>
      );
    }
  }, [renderer, widget, isLoading]);

  return (
    <div className={cn('w-full', className)}>
      {content}
    </div>
  );
};

WidgetRenderer.displayName = 'WidgetRenderer';
